<template>
  <div class="confirmation-dialog">
    <q-dialog ref="dialog" :persistent="ui.persistent">
      <Grid class="dialog-content bg-white-1 q-pa-xl">
        <CloseIcon v-if="!ui.noCloseIcon" :disable="ui.loading" class="close-icon" @click="hide"></CloseIcon>

        <slot name="title">
          <div class="text-body-3 text-secondary-1 text-center title">
            {{ ui.title }}
          </div>
        </slot>

        <slot name="body">
          <div v-if="ui.body" class="text-body-1 text-secondary-1 text-center body q-pt-md">
            {{ ui.body }}
          </div>
        </slot>

        <Grid
          v-if="!ui.noCancel && !ui.noConfirm"
          gaf="column"
          gap="var(--r-space-md)"
          justify-content="center"
          class="q-pt-xl"
        >
          <slot name="cancel-btn">
            <RBtn v-if="!ui.noCancel" :disable="ui.loading" :type="ui.cancelBtnType" @click="onCancelClicked">
              {{ ui.cancelLabel }}
            </RBtn>
          </slot>

          <slot name="confirm-btn">
            <RBtn v-if="!ui.noConfirm" :loading="ui.loading" :type="ui.confirmBtnType" @click="onConfirmClicked">
              {{ ui.confirmLabel }}
            </RBtn>
          </slot>
        </Grid>
      </Grid>
    </q-dialog>
  </div>
</template>

<script>
import CloseIcon from '@/ui/icons/CloseIcon'

export default {
  components: {
    CloseIcon
  },

  props: {
    title: String,
    body: String,
    loading: Boolean,
    noCancel: Boolean,
    noConfirm: Boolean,
    noCloseIcon: Boolean,

    cancelLabel: {
      type: String,
      default: 'cancel'
    },

    confirmLabel: {
      type: String,
      default: 'Confirm'
    },

    cancelBtnType: {
      type: String,
      default: 'ghost'
    },

    confirmBtnType: {
      type: String,
      default: 'primary'
    },

    persistent: {
      type: Boolean,
      default: true
    }
  },

  data: () => ({
    ui: {
      title: null,
      body: null,
      cancelLabel: null,
      confirmLabel: null,
      cancelBtnType: null,
      confirmBtnType: null,
      loading: null,
      persistent: null,
      noCancel: null,
      noConfirm: null,
      noCloseIcon: null
    }
  }),

  watch: {
    $props: {
      deep: true,
      handler(props) {
        for ([key, value] of Object.entries(props)) {
          this.ui[key] = value
        }
      }
    }
  },

  created() {
    this.onConfirm = () => ({})
    this.onCancel = () => ({})
  },

  methods: {
    show(props = {}) {
      // access from parent
      this.ui.title = props?.title ?? this?.title
      this.ui.body = props?.body ?? this?.body
      this.ui.cancelLabel = props?.cancelLabel ?? this.cancelLabel
      this.ui.confirmLabel = props?.confirmLabel ?? this.confirmLabel
      this.ui.cancelBtnType = props?.cancelBtnType ?? this.cancelBtnType
      this.ui.confirmBtnType = props?.confirmBtnType ?? this.confirmBtnType
      this.ui.persistent = props?.persistent ?? this.persistent
      this.ui.noCancel = props?.noCancel ?? this.noCancel
      this.ui.noConfirm = props?.noConfirm ?? this.noConfirm
      this.ui.noCloseIcon = props?.noCloseIcon ?? this.noCloseIcon
      this.ui.loading = props?.loading ?? this.loading

      this.$refs.dialog.show()

      return {
        onConfirm: fn => (this.onConfirm = fn.bind(null, this.hide, this.setLoading)),
        onCancel: fn => (this.onCancel = fn.bind(null, this.hide, this.setLoading))
      }
    },

    hide() {
      this.$refs.dialog.hide()
    },

    setLoading(state = true) {
      this.ui.loading = state
    },

    onCancelClicked() {
      this.$emit('cancel', this.hide, this.setLoading)
      this.onCancel()
    },

    onConfirmClicked() {
      this.$emit('confirm', this.hide, this.setLoading)
      this.onConfirm()
    }
  }
}
</script>

<style lang="scss" scoped>
.dialog-content {
  position: relative;

  .title,
  .body {
    white-space: pre-wrap;
  }

  .body {
    line-height: var(--r-space-xl);
  }

  .close-icon {
    position: absolute;
    top: var(--r-space-sm);
    right: var(--r-space-sm);
    height: auto;
    width: auto;
  }
}
</style>

<docs>
  ### Confirmation dialog

  #### How to use it

  - import it and register it
  - you can either pass props in the component level or in the `.show` child component method
  - call `.show` child component method

  #### Simple example

```jsx
  <template>
    <div id="app">
      <ConfirmationDialog ref="dialog" />
      <RBtn @click="open">Open dialog</RBtn>
    </div>
  </template>

  <script>
    import ConfirmationDialog from "@/ui/ConfirmationDialog";

    export default {
      components: {
        ConfirmationDialog
      },

      methods: {
        open () {
          this.$refs.dialog.show().onConfirm(hide => hide())
        }
      }
    }
  </script>
```

  #### Complex example with props in the component

  ```jsx
  <template>
    <Grid gap="8px" id="app">
      <ConfirmationDialog
        ref="dialog"
        :title="title"
        :cancel-label="cancelLabel"
        :confirm-label="confirmLabel"
        :cancel-btn-type="cancelBtnType"
        :confirm-btn-type="confirmBtnType"
        :persistent="persistent"
        :no-cancel="noCancel"
        :no-confirm="noConfirm"
        :no-close-icon="noCloseIcon"
      />

      <RBtn @click="open">Open Dialog</RBtn>

      <hr />
      Set props before opening the dialog

      <RInput v-model="title" label="Title" no-hint/>
      <RInput v-model="cancelLabel" label="Cancel label" no-hint/>
      <RInput v-model="confirmLabel" label="Confirm label" no-hint/>
      <RDropdown
        v-model="cancelBtnType"
        label="Cancel btn type"
        :options="['primary', 'secondary', 'danger', 'ghost', 'text', 'tertiary']"
        no-hint
      />
      <RDropdown
        v-model="confirmBtnType"
        label="Confirm btn type"
        :options="['primary', 'secondary', 'danger', 'ghost', 'text', 'tertiary']"
        no-hint
      />
      <RCheckbox v-model="persistent" label="Persistent" />
      <RCheckbox v-model="noCancel" label="No cancel btn" />
      <RCheckbox v-model="noConfirm" label="No confirm btn" />
      <RCheckbox v-model="noCloseIcon" label="No close icon" />
    </Grid>
  </template>

  <script>
    import ConfirmationDialog from "@/ui/ConfirmationDialog";

    export default {
      components: {
        ConfirmationDialog
      },

      data: () => ({
        title: null,
        cancelLabel: null,
        confirmLabel: null,
        cancelBtnType: null,
        confirmBtnType: null,
        persistent: true,
        noCancel: false,
        noConfirm: false,
        noCloseIcon: false
      }),

      methods: {
        open () {
          this.$refs.dialog.show().onConfirm((hide, setLoading) => {
            setLoading()
            setTimeout(() => hide(), 1000)
          })
        }
      }
    }
  </script>
  ```

  #### Complex example by mixing props in the component and props in the `show` method

  ```jsx
  <template>
    <Grid gap="8px" id="app">
      <ConfirmationDialog
        ref="dialog"
        :cancel-btn-type="cancelBtnType"
        :confirm-btn-type="confirmBtnType"
      />

      <RBtn @click="open">Open Dialog</RBtn>

      <hr />
      Set props before opening the dialog

      <RDropdown
        v-model="cancelBtnType"
        label="Cancel btn type"
        :options="['primary', 'secondary', 'danger', 'ghost', 'text', 'tertiary']"
        no-hint
      />
      <RDropdown
        v-model="confirmBtnType"
        label="Confirm btn type"
        :options="['primary', 'secondary', 'danger', 'ghost', 'text', 'tertiary']"
        no-hint
      />
    </Grid>
  </template>

  <script>
    import ConfirmationDialog from "@/ui/ConfirmationDialog";

    export default {
      components: {
        ConfirmationDialog
      },

      data: () => ({
        cancelBtnType: null,
        confirmBtnType: null
      }),

      methods: {
        open () {
          this.$refs.dialog.show({
            title: 'Dialog title',
            cancelLabel: 'No',
            confirmLabel: 'Yes',
            noCloseIcon: true
          }).onConfirm((hide, setLoading) => {
            setLoading()
            setTimeout(() => hide(), 1000)
          })
        }
      }
    }
  </script>
  ```
</docs>
